import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { colors } from '../../../../styles/theme';

/** Por encima de mapa / header / footer branded; debajo del product tour. */
const LIGHTBOX_Z_INDEX = 4000;

const navButtonSx = (side) => ({
  position: 'absolute',
  top: '50%',
  [side]: 12,
  transform: 'translateY(-50%)',
  zIndex: 1,
  color: colors.textPrimary,
  bgcolor: 'rgba(255,255,255,0.08)',
  width: 40,
  height: 40,
  '&:hover': { color: colors.textPrimary, bgcolor: 'rgba(255,255,255,0.15)' },
});

/**
 * Foto ampliada a pantalla completa. Porta al body para no pelear z-index
 * con controles del mapa. Escala con contain: no deforma.
 */
export default function ListingImageLightbox({
  images = [],
  index = 0,
  onClose,
  onIndexChange,
}) {
  const list = Array.isArray(images) ? images.filter((image) => image?.url) : [];
  const current = list[index] || null;
  const canCycle = list.length > 1;

  const go = (delta) => {
    if (!canCycle) return;
    onIndexChange?.((index + delta + list.length) % list.length);
  };

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }
      if (!canCycle) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onIndexChange?.((index - 1 + list.length) % list.length);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        onIndexChange?.((index + 1) % list.length);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canCycle, index, list.length, onClose, onIndexChange]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  if (!current || typeof document === 'undefined') return null;

  return createPortal(
    <Box
      onClick={() => onClose?.()}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: LIGHTBOX_Z_INDEX,
        bgcolor: 'rgba(0,0,0,0.88)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'zoom-out',
        backdropFilter: 'blur(4px)',
      }}
    >
      <IconButton
        aria-label="Cerrar foto"
        onClick={(event) => {
          event.stopPropagation();
          onClose?.();
        }}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1,
          color: 'rgba(255,255,255,0.7)',
          '&:hover': { color: colors.textPrimary, bgcolor: 'transparent' },
        }}
      >
        <CloseIcon />
      </IconButton>

      {canCycle ? (
        <IconButton
          aria-label="Foto anterior"
          onClick={(event) => {
            event.stopPropagation();
            go(-1);
          }}
          sx={navButtonSx('left')}
        >
          <ChevronLeftIcon />
        </IconButton>
      ) : null}

      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          position: 'relative',
          width: 'min(calc(100vw - 112px), 1600px)',
          height: 'min(calc(100vh - 72px), 900px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'default',
        }}
      >
        <img
          src={current.url}
          alt="foto ampliada"
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
            borderRadius: 6,
          }}
        />
        {canCycle ? (
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'rgba(255,255,255,0.7)',
              fontSize: '11px',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              px: 1,
              py: 0.25,
              borderRadius: '4px',
              bgcolor: 'rgba(0,0,0,0.45)',
            }}
          >
            {index + 1} / {list.length}
          </Typography>
        ) : null}
      </Box>

      {canCycle ? (
        <IconButton
          aria-label="Foto siguiente"
          onClick={(event) => {
            event.stopPropagation();
            go(1);
          }}
          sx={navButtonSx('right')}
        >
          <ChevronRightIcon />
        </IconButton>
      ) : null}
    </Box>,
    document.body,
  );
}
